import React, { useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';

/**
 * Kullanıcının YEREL biçimde yazdığı sayıyı çözer; çözülemezse null.
 *
 * Neden: `type="number"` alanı ve ham `textField` süzgeci yerel biçimi tanımaz — TR kullanıcısı
 * "1500,50" yazınca ya tarayıcı değeri boşaltır ya da ASP.NET decimal bağlamayı reddeder (400;
 * GET hatası gösterilmediği için tablo SESSİZCE boş kalır); "1.500" ise 1,5 olarak bağlanır.
 *
 *   • en: virgül binlik, nokta ondalık ("1,500.50").
 *   • diğerleri (tr/ru/kk/az): nokta binlik, virgül ondalık ("1.500,50"); boşluk/NBSP binlik
 *     (ru/kk "1 500,50") atılır. Virgül yoksa 3'lü gruplu "1.500" BİNLİK sayılır; aksi hâlde
 *     nokta ondalıktır ("1500.5" — kopyala/yapıştır).
 * Dönen değer her zaman değişmez (invariant) JS sayısıdır → URL'de `1500.5`.
 */
export function parseLocaleNumber(raw, locale) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  let s = String(raw).replace(/[\s  ]/g, '');
  if (s === '') return null;
  const lang = String(locale || '').toLowerCase().split(/[-_]/)[0];
  if (lang === 'en') {
    s = s.replace(/,/g, '');
  } else if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, '');
  }
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Yerel biçimli alan: ham metni KENDİ durumunda tutar (aksi hâlde "1500," yazarken değer sayıya
// çevrilip geri basılır ve virgül kaybolurdu); her değişiklikte ham metni bildirir.
function LocaleNumberField({ initial, onText, ...rest }) {
  const [raw, setRaw] = useState(initial);
  return (
    <TextField
      {...rest}
      type="text"
      value={raw}
      onChange={(e) => { setRaw(e.target.value); onText(e.target.value); }}
    />
  );
}

/**
 * Returns a column descriptor with a min/max numeric filter UI.
 * Sends: field[between]=min,max | field[gte]=min | field[lte]=max
 *
 * Usage:
 *   const col = numericFilterColumn('benefitCount', t('benefit.count'), {
 *     decimals: 0,                  // integer mode (default 0)
 *     suffix: '₺',                  // optional adornment
 *     customBodyRender: (v) => ..., // optional value formatter
 *     minOnly: true,                // yalnız alt sınır (field[gte]) — arka uç yalnız gte okuyorsa
 *     locale: i18n.language,        // yerel biçimli giriş ("1.500,50"); gönderilen değer değişmez
 *   });
 *
 * Backend must accept gte/lte/between operators on the field; same convention
 * as dateFilterColumn. parseTableParams maps { op, val } to ?field[op]=val.
 */
export function numericFilterColumn(name, label, options = {}) {
  const {
    decimals = 0,
    suffix,
    customBodyRender,
    sort = true,
    // Backend orderBy switch anahtarı (ör. scoreAggregate → score); parseTableParams gönderir.
    sortKey,
    clientSide = false,
    // Yalnız alt sınır (`field[gte]`): arka uç yalnız gte okuyorsa (ör. toplu izin `cost[gte]`)
    // üst sınır alanı sunulmaz — lte/between orada sessizce yok sayılırdı.
    minOnly = false,
    // Verilirse (i18n.language) alanlar YEREL biçimi kabul eder (bkz. parseLocaleNumber):
    // type="text" + inputMode; gönderilen değer yine değişmez sayıdır.
    locale,
  } = options;

  const parseNum = (s) => {
    if (s === '' || s === null || s === undefined) return null;
    if (locale) return parseLocaleNumber(s, locale);
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  // Çip ve alanın yeniden açılışı için sayıyı yerel biçimde göster (yalnız locale verilmişse).
  const fmt = (n) => {
    if (!locale || typeof n !== 'number') return n;
    try {
      return new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: 20 }).format(n);
    } catch (e) {
      return String(n);
    }
  };

  const clientLogic = (value, filters) => {
    if (!filters || !filters.length || !filters[0]) return false;
    const f = filters[0];
    const v = parseNum(value);
    if (v === null) return true;
    if (f.op === 'between') return v < parseNum(f.val[0]) || v > parseNum(f.val[1]);
    if (f.op === 'gte') return v < parseNum(f.val);
    if (f.op === 'lte') return v > parseNum(f.val);
    return false;
  };

  return {
    name,
    label,
    options: {
      filter: true,
      sort,
      ...(sortKey ? { sortKey } : {}),
      filterType: 'custom',
      ...(customBodyRender ? { customBodyRender } : {}),
      customFilterListOptions: {
        render: (v) => {
          if (!v || !v[0]) return [];
          const f = v[0];
          if (f.op === 'between') return [`${label}: ${fmt(f.val[0])} — ${fmt(f.val[1])}`];
          if (f.op === 'gte') return [`${label} ≥ ${fmt(f.val)}`];
          if (f.op === 'lte') return [`${label} ≤ ${fmt(f.val)}`];
          return [];
        },
        update: (filterList, _filterPos, index) => {
          filterList[index] = [];
          return filterList;
        },
      },
      filterOptions: {
        logic: clientSide ? clientLogic : () => false,
        display: (fl, onChange, i, col) => {
          const val = fl[i]?.[0];
          const minStr = val?.op === 'between' ? (val.val?.[0] ?? '') : (val?.op === 'gte' ? (val.val ?? '') : '');
          const maxStr = val?.op === 'between' ? (val.val?.[1] ?? '') : (val?.op === 'lte' ? (val.val ?? '') : '');

          const apply = (s, e) => {
            const sn = parseNum(s);
            const en = minOnly ? null : parseNum(e);
            if (sn !== null && en !== null) fl[i] = [{ op: 'between', val: [sn, en] }];
            else if (sn !== null) fl[i] = [{ op: 'gte', val: sn }];
            else if (en !== null) fl[i] = [{ op: 'lte', val: en }];
            else fl[i] = [];
            onChange(fl[i], i, col);
          };

          const inputProps = decimals > 0
            ? { inputMode: 'decimal', step: Math.pow(10, -decimals) }
            : { inputMode: 'numeric', step: 1 };

          const slotProps = suffix
            ? { input: { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } }
            : undefined;

          const fieldProps = (which) => {
            const fieldLabel = `${label} (${which})`;
            const current = which === 'min' ? minStr : maxStr;
            const onText = (text) => (which === 'min' ? apply(text, maxStr) : apply(minStr, text));
            if (locale) {
              return {
                Component: LocaleNumberField,
                props: {
                  label: fieldLabel,
                  variant: 'standard',
                  size: 'small',
                  fullWidth: true,
                  initial: current === '' ? '' : String(fmt(current)),
                  onText,
                  inputProps: { inputMode: decimals > 0 ? 'decimal' : 'numeric' },
                  slotProps,
                },
              };
            }
            return {
              Component: TextField,
              props: {
                label: fieldLabel,
                type: 'number',
                variant: 'standard',
                size: 'small',
                fullWidth: true,
                value: current,
                onChange: (e) => onText(e.target.value),
                inputProps,
                slotProps,
              },
            };
          };

          const min = fieldProps('min');
          if (minOnly) return <min.Component {...min.props} />;

          const max = fieldProps('max');
          return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <min.Component {...min.props} />
              <span style={{ color: '#A0A8B4' }}>—</span>
              <max.Component {...max.props} />
            </div>
          );
        },
      },
    },
  };
}
